/**
 * Calm legal prompts shown where a provider records a high-risk event.
 * A missing catalogue citation remains visible as unverified; it is never inferred.
 */
export type LegalGuidanceMomentId =
  | 'incident-sirs-notice'
  | 'complaint-outcome-communication'
  | 'restrictive-practice-consent'
  | 'data-breach-ndb-assessment'
  | 'rostered-worker-expired-screening';

export type LegalGuidanceVerificationStatus = 'VERIFIED' | 'UNVERIFIED';

export interface LegalGuidanceMoment {
  title: string;
  copy: readonly [string, string?];
  citation: string;
  verificationStatus: LegalGuidanceVerificationStatus;
}

export const LEGAL_GUIDANCE_MOMENTS: Readonly<Record<LegalGuidanceMomentId, LegalGuidanceMoment>> =
  {
    'incident-sirs-notice': {
      title: 'If this may be SIRS-reportable',
      copy: [
        'Priority 1 incidents are notified within 24 hours of becoming aware.',
        'Priority 2 incidents are notified within 30 days of becoming aware.',
      ],
      citation: 'Aged Care Rules 2025 rr 165A-25(1), 165A-30(1)',
      verificationStatus: 'VERIFIED',
    },
    'complaint-outcome-communication': {
      title: 'Before closing this complaint',
      copy: [
        'Tell the complainant the outcome and reasons unless the complaint was anonymous.',
        'Record how the outcome was communicated so the closure record is complete.',
      ],
      citation: 'Aged Care Rules 2025 r 165-25',
      verificationStatus: 'VERIFIED',
    },
    'restrictive-practice-consent': {
      title: 'When recording a restrictive practice',
      copy: [
        'Keep consent and authorisation with the record, and link the behaviour support plan where it applies.',
        'For an emergency use, save what happened first and complete those follow-ups as soon as practicable.',
      ],
      // No verified consent/authorisation citation in the repository register.
      // Keep provider guidance neutral; do not turn an internal provenance gap into an alert.
      citation: '',
      verificationStatus: 'UNVERIFIED',
    },
    'data-breach-ndb-assessment': {
      title: 'NDB assessment timing',
      copy: [
        'Take all reasonable steps to complete the assessment within 30 days of becoming aware of reasonable grounds to suspect an eligible data breach.',
      ],
      citation: 'Privacy Act 1988 (Cth) ss 26WH, 26WK, 26WL',
      verificationStatus: 'VERIFIED',
    },
    'rostered-worker-expired-screening': {
      title: 'A rostered worker has expired screening',
      copy: [
        'Review the worker screening evidence and resolve the compliance record.',
        'Care is not blocked by this warning; follow the provider’s worker-screening obligation and record the follow-up.',
      ],
      citation:
        'UNVERIFIED — worker-screening obligation citation is not recorded in the statutory constants register.',
      verificationStatus: 'UNVERIFIED',
    },
  };
